<template>
  <v-app>
    <v-main :class="containerState">
      <v-container id="main-container" :fluid="true">
        <!-- Toolbar -->
        <v-app-bar id="upp-bar" color="white" :flat="true" class="px-4">
          <div class="d-flex align-center">
            <v-icon color="primary" class="mr-2" size="large">
              mdi-transit-connection-horizontal
            </v-icon>
            <div class="text-primary text-h5 font-weight-bold">
              {{ $t("labels.page-title") }}
            </div>
          </div>

          <v-spacer></v-spacer>
          <v-row class="d-flex justify-end align-center">
            <v-col
              cols="12"
              sm="12"
              md="10"
              lg="8"
              xl="6"
              class="d-flex align-center align-self-center"
            >
              <v-text-field
                v-model="search"
                class="mr-4 align-self-center"
                density="compact"
                :loading="false"
                variant="solo"
                type="text"
                :label="$t('labels.search')"
                :append-inner-icon="!search ? 'mdi-magnify' : ''"
                :clearable="true"
                :single-line="true"
                hide-details
              ></v-text-field>
            </v-col>
          </v-row>
          <!--Button Dialog Import-->
          <DialogImport
            :apiUrl="mfConfig.api"
            :tenantId="realm"
            :refreshList="getFlowsList"
            @activateSnackbar="activateSnackbar"
          />
          <!--Button-Dialog-Create-->
          <v-dialog v-model="dialogCreate" max-width="500px">
            <template v-slot:activator="{ props }">
              <v-btn
                color="secondary"
                v-bind="props"
                variant="elevated"
                class="text-white ml-2"
              >
                {{ $t("labels.create-flow") }}
              </v-btn>
            </template>
            <v-card>
              <v-overlay
                :absolute="overlayAbsolute"
                :value="overlayOnVersion"
                :opacity="0.8"
              >
                <v-progress-circular
                  indeterminate
                  size="32"
                ></v-progress-circular>
              </v-overlay>
              <v-toolbar color="secondary">
                <v-toolbar-title class="text-white">
                  {{ $t("labels.create-flow") }}
                </v-toolbar-title>
                <v-spacer></v-spacer>
                <v-btn color="white" icon="mdi-close" @click="closeCreateFlow">
                </v-btn>
              </v-toolbar>
              <v-card-text>
                <v-form ref="createForm" v-model="validation" lazy-validation>
                  <v-row>
                    <v-col cols="12">
                      <v-text-field
                        v-model="editedItem.name"
                        :label="$t('labels.name')"
                        :hint="$t('strings.flow-project-name')"
                        :persistent-hint="true"
                        variant="outlined"
                        :rules="nameRules"
                        required
                        density="compact"
                      ></v-text-field>
                    </v-col>
                    <v-col cols="12">
                      <v-select
                        :items="mashups"
                        item-title="title"
                        item-value="_id"
                        v-model="editedItem._id"
                        :label="$t('labels.mashup')"
                        :hint="$t('strings.flow-mashup-name')"
                        :persistent-hint="true"
                        variant="outlined"
                        @update:modelValue="getMashupVersion"
                        :rules="mashupNameRules"
                        required
                        density="compact"
                      ></v-select>
                    </v-col>
                  </v-row>
                </v-form>
              </v-card-text>
              <v-divider></v-divider>
              <v-card-actions>
                <v-spacer></v-spacer>
                <v-btn
                  color="primary darken-1"
                  variant="text"
                  @click="closeCreateFlow"
                  >{{ $t("labels.cancel") }}</v-btn
                >
                <v-btn
                  color="secondary darken-1"
                  variant="text"
                  :disabled="!validation"
                  @click="createFlowConfirm"
                  >{{ $t("labels.save") }}</v-btn
                >
              </v-card-actions>
            </v-card>
          </v-dialog>
        </v-app-bar>
        <!-- Table -->
        <v-card
          tile
          :flat="true"
          class="flex-grow-1 d-flex flex-column overflow-hidden table-wrapper"
        >
          <v-data-table
            v-model="selected"
            :headers="headers"
            :items="flows"
            :single-select="singleSelect"
            :sort-by="sortBy"
            item-value="_id"
            show-select
            :search="search"
            :loading="loader"
            hover
            fixed-header
            height="100%"
          >
            <!-- Actions Toolbar over table -->
            <template v-slot:[`header.name`]>
              <div class="d-inline-flex justify-start align-center">
                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon="mdi-refresh"
                      variant="plain"
                      color="secondary"
                      v-bind="props"
                      @click="getFlowsList()"
                    >
                    </v-btn>
                  </template>
                  <span>{{ $t("labels.refresh") }}</span>
                </v-tooltip>

                <v-tooltip location="top">
                  <template v-slot:activator="{ props }">
                    <v-btn
                      icon
                      variant="plain"
                      color="secondary"
                      v-bind="props"
                      @click="cycleSort()"
                    >
                      <v-icon>{{ sortIcon }}</v-icon>
                    </v-btn>
                  </template>
                  <span>{{ $t(`labels.sort-${sortOrder}`) }}</span>
                </v-tooltip>

                <v-tooltip top>
                  <template v-slot:activator="{ props }">
                    <span v-bind="props" class="d-block">
                      <v-btn
                        :disabled="disabledDeleteMultiple"
                        :class="showDeleteIcon"
                        icon="mdi-delete"
                        variant="plain"
                        color="secondary"
                        v-bind="props"
                        @click="deleteMultipleFlow()"
                      >
                      </v-btn>
                    </span>
                  </template>
                  <span v-if="disabledDeleteMultiple">{{
                    $t("strings.delete-multiple-flows-tooltip", selected.length)
                  }}</span>
                  <span v-else>{{ $t("labels.delete") }}</span>
                </v-tooltip>
              </div>
            </template>
            <!--END Actions Toolbar over table -->

            <!-- NO DATA template -->
            <template v-slot:[`no-data`]>
              <div class="table-no-data">
                <img
                  :src="mfConfig.storageImgs + '/commons/watermark-comic.png'"
                  width="200"
                />
                <p
                  class="text-black font-weight-bold text-h6 text-uppercase mb-3"
                >
                  {{ $t("strings.no-flows") }}
                </p>
                <p>{{ $t("strings.please-add-flows") }}</p>
              </div>
            </template>

            <!-- Table's row definition -->
            <template v-slot:[`item.name`]="{ item }">
              <v-tooltip location="top">
                <template v-slot:activator="{ props }">
                  <v-list-item
                    lines="two"
                    class="px-0 py-2 no-highlight"
                    v-bind="props"
                    @click="routeToFlow(item)"
                  >
                    <v-list-item-title
                      class="font-weight-medium"
                      :search="search"
                    >
                      <v-tooltip bottom :color="setColorStatus(item.status)">
                        <template v-slot:activator="{ props }">
                          <v-chip
                            size="small"
                            :outlined="setStyle(item.status)"
                            class="mb-1 mr-1 px-2"
                            :class="isOutlined(item.status)"
                            :color="setColorStatus(item.status)"
                            v-bind="props"
                          >
                            {{ item.status.charAt(0).toUpperCase() }}
                          </v-chip>
                        </template>
                        <span>{{
                          $t("strings.flow-status", {
                            status: item.status.toUpperCase(),
                          })
                        }}</span>
                      </v-tooltip>
                      {{ item.name }}
                    </v-list-item-title>
                    <v-list-item-title class="mt-1">
                      <v-chip
                        class="mr-8 text-white opacity-100"
                        size="x-small"
                        label
                        variant="elevated"
                        :color="
                          loggedUserId === item.owner ? 'primary' : '#3d8ecc'
                        "
                      >
                        <span v-if="loggedUserId === item.owner">
                          {{ $t("labels.owner") }}
                        </span>
                        <span v-else>{{ $t("labels.editor") }}</span>
                      </v-chip>
                      <span class="font-weight-medium opacity-50">
                        {{ item.project.title }}&nbsp;
                        {{ item.project.version.name }}
                      </span>
                    </v-list-item-title>
                  </v-list-item>
                </template>
                <span>{{ $t("labels.open") }} {{ item.name }}</span>
              </v-tooltip>
            </template>
            <template v-slot:[`item.functions`]="{ item }">
              <v-spacer></v-spacer>
              <v-menu location="bottom left">
                <template v-slot:activator="{ props }">
                  <v-btn icon="mdi-dots-vertical" variant="flat" v-bind="props">
                  </v-btn>
                </template>
                <v-list density="default">
                  <v-tooltip
                    location="left"
                    :disabled="item.owner === loggedUserId"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="d-block">
                        <v-list-item
                          :disabled="item.owner !== loggedUserId"
                          @click="duplicateFlowProject(item)"
                        >
                          <template v-slot:prepend>
                            <v-icon color="primary" class="opacity-100"
                              >mdi-content-duplicate</v-icon
                            >
                          </template>
                          <v-list-item-title>{{
                            $t("labels.duplicate")
                          }}</v-list-item-title>
                        </v-list-item>
                      </span>
                    </template>
                    <span>{{ $t("strings.duplicate-tooltip") }}</span>
                  </v-tooltip>
                  <DialogShare
                    :share-form-config="mfConfig"
                    :realm="realm"
                    :itemToShare="item"
                  ></DialogShare>
                  <v-tooltip
                    location="left"
                    :disabled="item.owner === loggedUserId"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="d-block">
                        <v-list-item
                          :disabled="item.owner !== loggedUserId"
                          @click.stop="exportFlow(item)"
                        >
                          <template v-slot:prepend>
                            <v-icon color="primary" class="opacity-100"
                              >mdi-download</v-icon
                            >
                          </template>
                          <v-list-item-title>{{
                            $t("labels.export")
                          }}</v-list-item-title>
                        </v-list-item>
                      </span>
                    </template>
                    <span>{{ $t("strings.export-tooltip") }}</span>
                  </v-tooltip>
                  <v-tooltip
                    location="left"
                    :disabled="item.owner === loggedUserId"
                  >
                    <template v-slot:activator="{ props }">
                      <span v-bind="props" class="d-block">
                        <v-list-item
                          :disabled="item.owner !== loggedUserId"
                          @click="deleteFlow(item)"
                        >
                          <template v-slot:prepend>
                            <v-icon color="primary" class="opacity-100"
                              >mdi-delete</v-icon
                            >
                          </template>
                          <v-list-item-title>{{
                            $t("labels.delete")
                          }}</v-list-item-title>
                        </v-list-item>
                      </span>
                    </template>
                    <span>{{ $t("strings.delete-tooltip") }}</span>
                  </v-tooltip>
                </v-list>
              </v-menu>
            </template>
            <!-- END Table's row definition -->
          </v-data-table>
        </v-card>
        <!-- Snackbar-->
        <v-snackbar v-model="snackbar" top color="green" timeout="5000">
          {{ snackbarText }}
          <template v-slot:actions="{ props }">
            <v-btn
              color="white"
              variant="text"
              v-bind="props"
              @click="snackbar = false"
              >OK</v-btn
            >
          </template>
        </v-snackbar>
        <!--Dialog Delete-->
        <v-dialog v-model="dialogDelete" max-width="500px">
          <v-card>
            <v-toolbar :flat="true" color="secondary">
              <v-toolbar-title class="text-white">
                {{ $t("labels.delete") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon="mdi-close" @click="closeDeleteFlow">
              </v-btn>
            </v-toolbar>
            <v-card-text>
              <p class="text-h6">
                {{ $t("strings.delete-flow-question", numDelete) }}
              </p>
            </v-card-text>
            <v-divider></v-divider>
            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeDeleteFlow"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                color="secondary darken-1"
                variant="text"
                @click="deleteConfirm"
              >
                {{ $t("labels.delete") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>
        <!--Dialog Duplicate-->
        <v-dialog v-model="dialogDuplicate" max-width="500px">
          <v-card>
            <v-toolbar :flat="true" color="secondary">
              <v-toolbar-title class="text-white">
                {{ $t("labels.duplicate") }}
              </v-toolbar-title>
              <v-spacer></v-spacer>
              <v-btn color="white" icon="mdi-close" @click="closeDuplicateFlow">
              </v-btn>
            </v-toolbar>

            <v-card-text>
              <p class="text-h6">
                {{ $t("strings.duplicate-flow-question") }}
              </p>
            </v-card-text>
            <v-divider></v-divider>

            <v-card-actions>
              <v-spacer></v-spacer>
              <v-btn
                color="primary darken-1"
                variant="text"
                @click="closeDuplicateFlow"
              >
                {{ $t("labels.cancel") }}
              </v-btn>
              <v-btn
                color="secondary darken-1"
                variant="text"
                @click="duplicateFlowConfirm"
              >
                {{ $t("labels.duplicate") }}
              </v-btn>
            </v-card-actions>
          </v-card>
        </v-dialog>

        <v-overlay :value="overlay" z-index="100">
          <v-progress-circular indeterminate size="64"></v-progress-circular>
        </v-overlay>
      </v-container>
    </v-main>
  </v-app>
</template>

<script>
import events from "@/mixins/events.js";
import utils from "@/mixins/utils.js";

import FlowsList from "@/mixins/FlowsList.js";
import DialogImport from "@/components/DialogImport.vue";
import DialogShare from "@/components/DialogShare.vue";
export default {
  name: "FlowsList",
  components: { DialogImport, DialogShare },
  mixins: [utils, events, FlowsList],
  props: ["mfConfig", "realm"],
  data: function () {
    return {
      loader: false,
      overlay: false,
      snackbar: false,
      snackbarText: "",
      overlayOnVersion: false,
      overlayAbsolute: true,
      containerState: "expanded",
      username: "",
      search: "",
      nameRules: [(value) => !!value || this.$t("errors.flow-name-required")],
      mashupNameRules: [
        (value) => !!value || this.$t("errors.mashup-select-required"),
      ],
      validation: true,
      dialogDelete: false,
      dialogCreate: false,
      singleSelect: false,
      dialogDuplicate: false,
      selected: [],
      numDelete: 1,
      showDeleteIcon: "d-none",
      loggedUserId: "",
      headers: [
        {
          text: "",
          align: "start",
          sortable: false,
          value: "name",
        },
        {
          text: "",
          align: "end",
          sortable: false,
          value: "functions",
        },
      ],
      sortOrder: "desc",
      sortIcon: "mdi-sort-descending",
      sortBy: [{ key: "_id", order: "desc" }],
      flows: [],
      mashups: [],
      editedIndex: -1,
      editedItem: {
        name: "",
        _id: "",
        latestRelease: "",
        owner: "",
      },
      defaultItem: {
        name: "",
        _id: "",
        latestRelease: "",
        owner: "",
      },
    };
  },

  mounted() {
    this.loggedUserId = localStorage.getItem("loggedUserId");

    document.addEventListener("language-localstorage-changed", (event) => {
      this.setLocale(event.detail.storage);
    });

    this.containerState =
      localStorage.getItem("sidenav") === "opened" ? "reduced" : "expanded";

    document.addEventListener("sidenav-localstorage-changed", (event) => {
      this.containerState =
        event.detail.storage === "opened" ? "reduced" : "expanded";
    });
  },
  watch: {
    selected() {
      this.showDeleteIcon = this.selected.length > 0 ? "d-block" : "d-none";
    },
  },
  computed: {
    disabledDeleteMultiple() {
      return this.selected.some((id) => {
        const item = this.flows.find((el) => el._id === id);
        return item ? item.owner !== this.loggedUserId : false;
      });
    },
  },
  methods: {
    cycleSort() {
      if (this.sortOrder === "desc") {
        this.sortOrder = "asc";
        this.sortIcon = "mdi-sort-ascending";
        this.sortBy = [{ key: "_id", order: "asc" }];
      } else {
        this.sortOrder = "desc";
        this.sortIcon = "mdi-sort-descending";
        this.sortBy = [{ key: "_id", order: "desc" }];
      }
    },
  },
};
</script>
<style scoped>
.table-no-data {
  display: flex;
  flex-direction: column;
  justify-content: center;
  align-items: center;
}

.table-wrapper {
  min-height: 0;
}

main.expanded {
  margin-left: 56px;
}

main.expanded header#upp-bar {
  margin-left: 56px;
  width: calc(100% - 56px) !important;
}

main.reduced {
  margin-left: 256px;
}

main.reduced header#upp-bar {
  margin-left: 256px;
  width: calc(100% - 256px) !important;
}

#main-container {
  height: calc(100vh - 104px);
  overflow: hidden;
  background-color: white;
  display: flex;
  flex-direction: column;
}

.no-highlight :deep(.v-list-item__overlay) {
  background: transparent !important;
}

:deep(.v-table) {
  height: 100%;
}
</style>
